import { ENDPOINTS, getFullUrl } from "./config";
import { AddSaltPayload } from "./types";

export class InverntroyService {
  static getToken(): string | null {
    return localStorage.getItem("token");
  }
  static getRole(): string | null {
    return localStorage.getItem("auth_user");
  }

  static async getProductListing({
    page = 1,
    pageSize = 10,
    searchText = "",
  }: {
    page?: number;
    pageSize?: number;
    searchText?: string;
  }) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.INVENTORY.GETPRODUCTLISTING));
      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());
      if (searchText) {
        url.searchParams.append("name", searchText);
      }
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      const data = await response?.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to fetch products",
        data: [],
      };
    }
  }

  static async getProductDetailsInward(productId: string) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.GETPRODUCTDETAILSINWARD).replace(
          ":productId",
          productId,
        ),
      );
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      const data = await response?.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch supervisors",
        data: [],
      };
    }
  }

  static async getProductDetails(productId: string) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.GETPRODUCTDETAILS).replace(
          ":productId",
          productId,
        ),
      );
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      const data = await response?.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch supervisors",
        data: [],
      };
    }
  }

  static async getCategoryList() {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.INVENTORY.GETCATEGORYLIST));
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });
      const data = await response?.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to fetch categories",
        data: [],
      };
    }
  }
  static async deleteProduct(productId: string) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.DELETEPRODUCT).replace(
          ":productId",
          productId,
        ),
      );

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to delete supervisor",
      };
    }
  }
  static async deleteProductCategory(productId: string, categoryId: number) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.DELETECATEGORY) +
        `/${productId}` +
        `/${categoryId}`,
      );

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to remove Category",
      };
    }
  }

  static async addNewProductCategory(productId: string, categoryId: number) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.ADDCATEGORY) + `/${productId}`,
      );

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify({ categoryId }),
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to add product",
      };
    }
  }

  static async addNewProduct(productData: FormData) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.INVENTORY.CREATENEWPRODUCT));

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: productData,
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to add product",
      };
    }
  }

  static async updateNewProduct(productData: FormData, productId: string) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.UPDATEPRODUCT).replace(
          ":productId",
          `${productId}`,
        ),
      );

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: productData,
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to update product",
      };
    }
  }

  // --- PRODUCT V2 APIs ---

  static async createProductV2(productData: FormData) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.INVENTORY.CREATENEWPRODUCT));
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: productData,
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to create product",
      };
    }
  }

  static async updateProductBasicV2(productId: string, productData: FormData) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.UPDATEPRODUCT).replace(
          ":productId",
          productId,
        ),
      );
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: productData,
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to update basic details",
      };
    }
  }

  static async updateProductActivityV2(productId: string, isActive: boolean) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.UPDATEPRODUCT_ACTIVITY).replace(
          ":productId",
          productId,
        ),
      );
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify({ isActive }),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to update basic details",
      };
    }
  }

  static async updateProductPkgnV2(productId: string, data: any) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.UPDATEPRODUCT_PKGN).replace(
          ":productId",
          productId,
        ),
      );
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to update packaging",
      };
    }
  }

  static async updateProductInfoV2(productId: string, data: any) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.UPDATEPRODUCT_INFO).replace(
          ":productId",
          productId,
        ),
      );
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to update info",
      };
    }
  }

  static async updateProductTaxesV2(productId: string, data: any) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.UPDATEPRODUCT_TAXES).replace(
          ":productId",
          productId,
        ),
      );
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to update taxes",
      };
    }
  }

  static async updateProductListingCategoriesV2(productId: string, data: { addIds?: number[], removeIds?: number[] }) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.UPDATEPRODUCT_CATEGORIES).replace(
          ":productId",
          productId,
        ),
      );
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to update listing categories",
      };
    }
  }

  static async getLabTestListing({
    page = 1,
    pageSize = 10,
  }: {
    page?: number;
    pageSize?: number;
  }) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.INVENTORY.GETLABTESTLISTING));
      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      const data = await response?.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch supervisors",
        data: [],
      };
    }
  }

  static async addNewLabTest(labTestData: any) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.INVENTORY.CREATENEWLABTEST));
      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(labTestData),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to add lab test",
        data: [],
      };
    }
  }

  static async getLabTestDetails(labTestId: string) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.GETLABTESTDETAILS).replace(
          ":labtest",
          labTestId,
        ),
      );
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });
      const data = await response?.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch supervisors",
        data: [],
      };
    }
  }

  static async deleteLabTest(labTestId: number) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.DELETELABTEST).replace(
          ":labTestId",
          labTestId.toString(),
        ),
      );

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to delete supervisor",
      };
    }
  }

  static async updateLabTest(labTestData: any, labTestId: string) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.UPDATELABTEST).replace(
          ":labTestId",
          labTestId,
        ),
      );
      const response = await fetch(url.toString(), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(labTestData),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to update lab test",
        data: [],
      };
    }
  }

  static async getLabTestCategory() {
    const token = this.getToken();

    try {
      const response = await fetch(
        getFullUrl(ENDPOINTS.INVENTORY.GETLABTESTCATEGORY),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.message || "Failed to fetch lab test categories",
        );
      }

      return result.data; // assuming structure { success, message, data: [...] }
    } catch (error) {
      console.error("Error fetching lab test categories:", error);
      return [];
    }
  }

  static async addNewWholeSaler(data: any) {
    const token = this.getToken();
    try {
      const URL = getFullUrl(ENDPOINTS.WHOLESALERS.ADDNEWWHOLESALER);
      const response = await fetch(URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete Wholesaler");
      }
      return result;
    } catch (error) {
      console.error("Error deleting Wholesaler :", error);
      throw error;
    }
  }

  static async getWholeSalerDetails(id: string) {
    const token = this.getToken();
    try {
      const URL = getFullUrl(ENDPOINTS.WHOLESALERS.GETWHOLESALERDETAILS);
      const response = await fetch(URL.replace(":id", id), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete Wholesaler");
      }
      return result;
    } catch (error) {
      console.error("Error deleting Wholesaler :", error);
      throw error;
    }
  }

  static async getWholeSalerListing(page = 1, pageSize = 1, filter = {}) {
    const token = this.getToken();
    const stringFilter = JSON.stringify({ name: filter });
    try {
      const url = new URL(getFullUrl(ENDPOINTS.WHOLESALERS.GETWHOLESALERLIST));
      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());
      url.searchParams.append("filters", stringFilter);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete Wholesaler");
      }
      return result.data;
    } catch (error) {
      console.error("Error deleting Wholesaler :", error);
      throw error;
    }
  }

  static async getWholeSalerOptions() {
    const token = this.getToken();

    try {
      const response = await fetch(
        getFullUrl(ENDPOINTS.WHOLESALERS.GETWHOLESALEROPTIONS),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch Wholesaler options");
      }
      return result.data;
    } catch (error) {
      console.error("Error fetching Wholesaler options:", error);
      return [];
    }
  }

  static async updateWholeSalerDetails(id: string, data: any) {
    const token = this.getToken();
    try {
      const URL = getFullUrl(ENDPOINTS.WHOLESALERS.UPDATEWHOLESALERDETAILS);
      const response = await fetch(URL.replace(":id", id), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete Wholesaler");
      }
      return result;
    } catch (error) {
      console.error("Error deleting Wholesaler :", error);
      return [];
    }
  }

  static async deleteWholeSaler(id: any) {
    const token = this.getToken();

    try {
      const URL = getFullUrl(ENDPOINTS.WHOLESALERS.DELETEWHOLESALER);
      const response = await fetch(URL.replace(":id", id), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete Wholesaler");
      }
      return result;
    } catch (error) {
      console.error("Error deleting Wholesaler :", error);
      throw error;
    }
  }

  static async addNewmanufacturer(data: any) {
    const token = this.getToken();
    try {
      const URL = getFullUrl(ENDPOINTS.MANUFACTURER.ADDNEWMANUFACTURER);
      const response = await fetch(URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete Wholesaler");
      }
      return result;
    } catch (error) {
      console.error("Error deleting Wholesaler :", error);
      throw error;
    }
  }

  static async getWholeSalerManufacturerDetails(id: string) {
    const token = this.getToken();
    try {
      const URL = getFullUrl(ENDPOINTS.MANUFACTURER.GETMANUFACTURERDETAILS);
      const response = await fetch(URL.replace(":id", id), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete Wholesaler");
      }
      return result;
    } catch (error) {
      console.error("Error deleting Wholesaler :", error);
      throw error;
    }
  }

  static async getManufacturerListing(page = 1, pageSize = 1, searchTerm = "") {
    const token = this.getToken();
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.MANUFACTURER.GETMANUFACTURERLIST),
      );
      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());
      url.searchParams.append("searchText", searchTerm);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete Wholesaler");
      }
      return result.data;
    } catch (error) {
      console.error("Error deleting Wholesaler :", error);
      throw error;
    }
  }

  static async updateManufacturerDetails(id: string, data: any) {
    const token = this.getToken();
    try {
      const URL = getFullUrl(ENDPOINTS.MANUFACTURER.UPDATEMANUFACTURERDETAILS);
      const response = await fetch(URL.replace(":manufacturerId", id), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete Wholesaler");
      }
      return result;
    } catch (error) {
      console.error("Error deleting Wholesaler :", error);
      return [];
    }
  }

  static async deleteManufacturer(id: any) {
    const token = this.getToken();

    try {
      const URL = getFullUrl(ENDPOINTS.MANUFACTURER.DELETEMANUFACTURER);
      const response = await fetch(URL.replace(":id", id), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete Wholesaler");
      }
      return result;
    } catch (error) {
      console.error("Error deleting Wholesaler :", error);
      throw error;
    }
  }

  static async getManufacturerOptions() {
    const token = this.getToken();

    try {
      const response = await fetch(
        getFullUrl(ENDPOINTS.MANUFACTURER.GETMANUFACTUREROPTIONS),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch Wholesaler options");
      }
      return result.data;
    } catch (error) {
      console.error("Error fetching Wholesaler options:", error);
      return [];
    }
  }

  static async addProductToStock(data: any) {
    const token = this.getToken();

    try {
      const response = await fetch(getFullUrl(ENDPOINTS.STOCKS.ADDTOSTOCK), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to add stock");
      }
      return result;
    } catch (error) {
      console.error("Error Adding to stock:", error);
      return [];
    }
  }

  static async getDistincitInwardStocks() {
    const token = this.getToken();

    try {
      const response = await fetch(
        getFullUrl(ENDPOINTS.STOCKS.GETDISTINCTINWARDSTOCKS),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to add stock");
      }
      return result;
    } catch (error) {
      console.error("Error Adding to stock:", error);
      return [];
    }
  }

  static async getInWardProductDetails(id: string) {
    const token = this.getToken();

    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.STOCKS.GETINWARDPRODUCTDETAILS).replace(":id", id),
      );
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to add stock");
      }
      return result;
    } catch (error) {
      console.error("Error Adding to stock:", error);
      return [];
    }
  }
  static async updateProductSellingPrice(data: any) {
    const token = this.getToken();

    try {
      const url = new URL(getFullUrl(ENDPOINTS.STOCKS.UPDATESELLINGPRICE));
      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to add stock");
      }
      return result;
    } catch (error) {
      console.error("Error Adding to stock:", error);
      return [];
    }
  }

  static async AddNewSalt(payload: AddSaltPayload) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.INVENTORY.ADDNEWSALT));
      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to add new salt");
      }

      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "An unknown error occurred",
      };
    }
  }

  static async updateSalt(saltId: string, saltData: { name: string }) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.UPDATESALT).replace(":saltId", saltId),
      );
      const response = await fetch(url.toString(), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(saltData), // Include the salt data in request body
      });

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to update salt",
        data: [],
      };
    }
  }

  static async deleteSalt(saltId: string) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.INVENTORY.DELETESALT).replace(":saltId", saltId),
      );
      const response = await fetch(url.toString(), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to delete salt",
        data: [],
      };
    }
  }

  static async getInventryList(
    page = 1,
    pageSize = 10,
    searchText = "",
    filter = "",
  ) {
    const token = this.getToken();
    try {
      const url = new URL(getFullUrl(ENDPOINTS.STOCKS.GETALLINVENTRYLIST));
      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());
      url.searchParams.append("searchText", searchText);
      url.searchParams.append("filter", filter);
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to add stock");
      }
      return result;
    } catch (error) {
      console.error("Error Adding to stock:", error);
      return [];
    }
  }

  static async getInwardStockListingForClinic({
    clinicId,
    page = 1,
    pageSize = 10,
  }: {
    clinicId: string;
    page?: number;
    pageSize?: number;
  }) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.STOCKS.GETINWARDSTOCKFORCLINIC).replace(
          ":clinicId",
          clinicId,
        ),
      );
      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      const data = await response?.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to clinic's inward stock",
        data: [],
      };
    }
  }

  static async getExpiryAlerts({
    page = 1,
    pageSize = 1000,
    expireIn = null,
    clinicId = null,
    completeData = false,
  }: {
    page?: number;
    pageSize?: number;
    expireIn: string | null;
    clinicId: string | null | undefined;
    completeData?: boolean;
  }) {
    const token = this.getToken();
    try {
      const url = new URL(getFullUrl(ENDPOINTS.STOCKS.GETEXPIRYALERTS));
      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());
      if (expireIn) {
        url.searchParams.append("expireIn", expireIn);
      }
      if (clinicId) {
        url.searchParams.append("clinicId", clinicId);
      }
      if (completeData) {
        url.searchParams.append("completeData", completeData.toString());
      }
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok)
        throw new Error(result.message || "Failed fetch expiry alerts");
      return result;
    } catch (error) {
      console.error("Error Adding to stock:", error);
      return [];
    }
  }

  static async getInvoiceHistory(
    page: number,
    pageSize: number,
    searchTerm?: string,
  ) {
    try {
      const token = this.getToken();
      const url = new URL(getFullUrl(ENDPOINTS.STOCKS.GETINVOICEHISTORY));
      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());
      const filters = {
        invoiceNumber: searchTerm,
      };
      url.searchParams.append("filters", JSON.stringify(filters));
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.message || "Failed fetch invoice history");
      return result;
    } catch (error) {
      console.error("Error Adding to stock:", error);
      return [];
    }
  }

  static async getInvoiceHistoryDetails(invoiceNumber: string) {
    try {
      const token = this.getToken();
      const url = new URL(
        getFullUrl(ENDPOINTS.STOCKS.GETINVOICEHISTORYDETAILS),
      );
      url.searchParams.append("invoiceNumber", invoiceNumber);
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(
          result.message || "Failed fetch invoice history details",
        );
      return result;
    } catch (error) {
      console.error("Error Adding to stock:", error);
      return [];
    }
  }

  static async updatePaymentStatus(
    inwardStockId: string,
    paymentStatus: string,
  ) {
    try {
      const token = this.getToken();
      const response = await fetch(
        getFullUrl(ENDPOINTS.STOCKS.UPDATEPAYMENTSTATUS),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            inwardStockId,
            paymentStatus,
          }),
        },
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(
          result.message || result.error || "Failed to update payment status",
        );
      }
      return result;
    } catch (error) {
      console.error("Error updating payment status:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update payment status",
      };
    }
  }

  static async CHECKINVOICENUMBERAVAILABILITY(invoiceNumber: string) {
    try {
      const token = this.getToken();
      const url = new URL(
        getFullUrl(ENDPOINTS.STOCKS.CHECKINVOICENUMBERAVAILABILITY),
      );
      url.searchParams.append("invoiceNumber", invoiceNumber);
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(
          result.message || "Failed fetch invoice history details",
        );
      return result;
    } catch (error) {
      console.error("Error Adding to stock:", error);
      return [];
    }
  }

  static async CHECKBATCHNUMBERAVAILABILITY(batchNumber: string) {
    try {
      const token = this.getToken();
      const url = new URL(
        getFullUrl(ENDPOINTS.STOCKS.CHECKBATCHNUMBERAVAILABILITY),
      );
      url.searchParams.append("invoiceNumber", batchNumber);
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(
          result.message || "Failed fetch invoice history details",
        );
      return result;
    } catch (error) {
      console.error("Error Adding to stock:", error);
      return [];
    }
  }

  static async getInvoiceChargesHeads({
    search = "",
    action = "",
  }: { search?: string; action?: string } = {}) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.INVOICECHARGESHEAD.LIST));
      if (search) url.searchParams.append("search", search);
      if (action) url.searchParams.append("action", action);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      const result = await response.json();
      if (!response.ok)
        throw new Error(result.message || "Failed to fetch invoice charges heads");
      return result;
    } catch (error) {
      console.error("Error fetching invoice charges heads:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch invoice charges heads",
        data: [],
      };
    }
  }

  static async createInvoiceChargesHead(data: {
    headType: string;
    headHsnNumber?: string | null;
    headGst?: number | null;
    action: string;
  }) {
    try {
      const response = await fetch(
        getFullUrl(ENDPOINTS.INVOICECHARGESHEAD.CREATE),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.getToken()}`,
          },
          body: JSON.stringify(data),
        },
      );

      const result = await response.json();
      if (!response.ok)
        throw new Error(result.message || "Failed to create invoice charges head");
      return result;
    } catch (error) {
      console.error("Error creating invoice charges head:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to create invoice charges head",
      };
    }
  }

  static async updateInvoiceChargesHead(
    id: number | string,
    data: {
      headType?: string;
      headHsnNumber?: string | null;
      headGst?: number | null;
      action?: string;
    },
  ) {
    try {
      const response = await fetch(
        getFullUrl(ENDPOINTS.INVOICECHARGESHEAD.UPDATE).replace(
          ":id",
          id.toString(),
        ),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.getToken()}`,
          },
          body: JSON.stringify(data),
        },
      );

      const result = await response.json();
      if (!response.ok)
        throw new Error(result.message || "Failed to update invoice charges head");
      return result;
    } catch (error) {
      console.error("Error updating invoice charges head:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update invoice charges head",
      };
    }
  }

  static async handleUpdateLabtestStatusByClinic(data: {
    orderId: string;
    orderStatus: string;
  }) {
    try {
      const token = this.getToken();
      const url = new URL(
        getFullUrl(ENDPOINTS.STOCKS.UPDATELABTESTSTATUSBYCLINIC).replace(
          ":orderId",
          data.orderId,
        ),
      );
      const response = await fetch(url.toString(), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          status: data.orderStatus,
        }),
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(
          result.message || "Failed fetch invoice history details",
        );
      return result;
    } catch (error) {
      console.error("Error Adding to stock:", error);
      return [];
    }
  }

  static async getInvoiceHistoryForClinic(
    page: number,
    pageSize: number,
    clinicId: string,
    searchTerm?: string | null,
    completeData?: boolean,
  ) {
    try {
      const token = this.getToken();
      const url = new URL(getFullUrl(ENDPOINTS.STOCKS.GETINVOICEHISTORY));

      // Add page and pageSize parameters
      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());
      url.searchParams.append("clinicId", clinicId);
      if (completeData) {
        url.searchParams.append("completeData", completeData.toString());
      }
      // Add filters parameter with clinicId
      const filters = {
        clinicId: clinicId,
        invoiceNumber: searchTerm,
      };
      url.searchParams.append("filters", JSON.stringify(filters));

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (!response.ok)
        throw new Error(result.message || "Failed fetch invoice history");
      return result;
    } catch (error) {
      console.error("Error fetching invoice history:", error);
      return [];
    }
  }

  static async addNewMarketer(data: FormData) {
    const token = this.getToken();
    try {
      const URL = getFullUrl(ENDPOINTS.MARKETER.ADDNEWMARKETER);
      const response = await fetch(URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: data,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to add marketer");
      }
      return result;
    } catch (error) {
      console.error("Error adding marketer :", error);
      throw error;
    }
  }

  static async getWholeSalerMarketerDetails(id: string) {
    const token = this.getToken();
    try {
      const URL = getFullUrl(ENDPOINTS.MARKETER.GETMARKETERDETAILS);
      const response = await fetch(URL.replace(":id", id), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch marketer");
      }
      return result;
    } catch (error) {
      console.error("Error fetching marketer :", error);
      throw error;
    }
  }

  static async getMarketerListing(page = 1, pageSize = 1, searchTerm = "") {
    const token = this.getToken();
    try {
      const url = new URL(getFullUrl(ENDPOINTS.MARKETER.GETMARKETERLIST));
      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());
      url.searchParams.append("searchText", searchTerm);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch marketer list");
      }
      return result.data;
    } catch (error) {
      console.error("Error fetching marketer lists :", error);
      throw error;
    }
  }

  static async updateMarketerDetails(id: string, data: FormData) {
    const token = this.getToken();
    try {
      const URL = getFullUrl(ENDPOINTS.MARKETER.UPDATEMARKETERDETAILS);
      const response = await fetch(URL.replace(":marketerId", id), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: data,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to update marketer");
      }
      return result;
    } catch (error) {
      console.error("Error updating marketer :", error);
      return [];
    }
  }

  static async deleteMarketer(id: any) {
    const token = this.getToken();

    try {
      const URL = getFullUrl(ENDPOINTS.MARKETER.DELETEMARKETER);
      const response = await fetch(URL.replace(":id", id), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete Marketer");
      }
      return result;
    } catch (error) {
      console.error("Error deleting marketer :", error);
      throw error;
    }
  }

  static async getMarketerOptions() {
    const token = this.getToken();

    try {
      const response = await fetch(
        getFullUrl(ENDPOINTS.MARKETER.GETMARKETEROPTIONS),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch marketer options");
      }
      return result.data;
    } catch (error) {
      console.error("Error fetching marketer options:", error);
      return [];
    }
  }

  static async assignDriver(data: any) {
    const token = this.getToken();
    try {
      const URL = getFullUrl(ENDPOINTS.ORDERS.ASSIGN_DRIVER);
      const response = await fetch(URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to assign driverr");
      }
      return result;
    } catch (error) {
      console.error("Error assigning driver :", error);
      return [];
    }
  }

  static async getExistingWholesaler(dlNumber: string) {
    const token = this.getToken();

    try {
      const URL = getFullUrl(ENDPOINTS.WHOLESALERS.CHECKEXISTINGDLNUMBER);
      const response = await fetch(`${URL}/${dlNumber}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch dlnumber");
      }
      return result;
    } catch (error) {
      console.error("Error fetching dlnumber:", error);
      return [];
    }
  }

  static async getExistingCategories() {
    const token = this.getToken();

    try {
      const URL = getFullUrl(ENDPOINTS.INVENTORY.GETALLCATEGORIES);
      const response = await fetch(URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch categories");
      }
      return result;
    } catch (error) {
      console.error("Error fetching categories:", error);
      return [];
    }
  }
  static async createStockRequest(data: any): Promise<any> {
    const token = this.getToken();
    try {
      const url = getFullUrl(ENDPOINTS.INVENTORY.CREATESTOCKREQUEST);
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to create stock request");
      }
      return result;
    } catch (error) {
      console.error("Error creating stock request:", error);
      throw error;
    }
  }

  static async getStockRequestListing(page: number, pageSize: number, clinicId?: string): Promise<any> {
    const token = this.getToken();
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });
      if (clinicId) {
        params.set("clinicId", clinicId);
      }
      const url = getFullUrl(ENDPOINTS.INVENTORY.GETSTOCKREQUESTLISTING);
      const response = await fetch(`${url}?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to get stock request listing");
      }
      return result;
    } catch (error) {
      console.error("Error getting stock request listing:", error);
      throw error;
    }
  }

  static async deleteStockRequest(id: string): Promise<any> {
    const token = this.getToken();
    try {
      const url = getFullUrl(ENDPOINTS.INVENTORY.DELETESTOCKREQUEST.replace(":id", id));
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete stock request");
      }
      return result;
    } catch (error) {
      console.error("Error deleting stock request:", error);
      throw error;
    }
  }
  static async getStockReport({
    marketerId,
    manufacturerId,
    fromDate,
    toDate,
    page = 1,
    pageSize = 10,
    clinicId,
    productName,
  }: {
    marketerId?: string;
    manufacturerId?: string;
    fromDate?: string;
    toDate?: string;
    page?: number;
    pageSize?: number;
    clinicId?: string;
    productName?: string;
  }) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.REPORT.GET_STOCK_REPORT));
      if (marketerId) url.searchParams.append("marketerId", marketerId);
      if (manufacturerId)
        url.searchParams.append("manufacturerId", manufacturerId);
      if (fromDate) url.searchParams.append("fromDate", fromDate);
      if (toDate) url.searchParams.append("toDate", toDate);
      if (clinicId) url.searchParams.append("clinicId", clinicId);
      if (productName) url.searchParams.append("productName", productName);
      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to fetch stock report",
      };
    }
  }
}
